import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { CheckCircle2, ChevronRight, X, Rocket, Image as ImageIcon, Briefcase,
         Camera, Calendar, Sparkles, UserPlus, Download, Share, Circle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useModalBack } from "@/lib/modalBack";
import { isIos, isInstalled } from "@/components/InstallPrompt";

const STEP_ICONS = { logo: ImageIcon, first_job: Briefcase, three_photos: Camera,
                     scheduled: Calendar, tried_360: Sparkles, crew_invited: UserPlus };

// "Get set up" — 6 steps for new companies, ticked from real server data
// (never from taps). Each row opens the exact screen with prefills. At 6/6 the
// card congratulates and hides itself; Dismiss moves it to More > Help.
export default function GetSetupCard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [importDismissed, setImportDismissed] = useState(() => {
    try { return localStorage.getItem("bp_setup_import_dismissed") === "1"; } catch { return false; }
  });
  const [st, setSt] = useState(null);
  const [installOpen, setInstallOpen] = useState(false);
  const [installed, setInstalled] = useState(true);
  useModalBack(installOpen, () => setInstallOpen(false));

  const load = () => api.get("/setup/status").then(({ data }) => setSt(data)).catch(() => {});
  // Server flag wins: installed on ANY device hides the install row everywhere.
  useEffect(() => { if (user?.app_installed) setInstalled(true); }, [user?.app_installed]);
  useEffect(() => {
    setInstalled((prev) => prev || isInstalled());
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    load();
    // Re-tick steps from data whenever the app returns to the foreground.
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener("focus", onVis);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("focus", onVis);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const dismiss = () => {
    api.post("/setup/hide", { hidden: true }).catch(() => {});
    setSt((p) => (p ? { ...p, dismissed: true } : p));
  };

  // 6/6: brief "You're set up" confirmation, then the card hides itself.
  useEffect(() => {
    if (!st?.all_done || st.dismissed) return undefined;
    const timer = setTimeout(dismiss, 8000);
    return () => clearTimeout(timer);
  }, [st?.all_done, st?.dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  const proj = st?.first_project || null;
  const tomorrow = new Date(Date.now() + 864e5).toLocaleDateString("en-CA");
  const runStep = (key) => {
    if (key === "logo") navigate("/app/brand?logo=1");
    else if (key === "first_job") navigate("/app/projects/new");
    else if (key === "three_photos") window.dispatchEvent(new CustomEvent("bf:open-camera",
      { detail: { projectId: proj?.id, projectName: proj?.name || "" } }));
    else if (key === "scheduled") navigate(`/app/schedule?add=1${proj ? `&project=${proj.id}` : ""}&date=${tomorrow}&daypart=morning`);
    else if (key === "tried_360") window.dispatchEvent(new CustomEvent("bf:open-assistant",
      { detail: { mode: "update", projectId: proj?.id || null, projectName: proj?.name || "" } }));
    else if (key === "crew_invited") navigate("/app/crew?invite=1");
  };

  const done = st?.progress?.done ?? 0;
  const total = st?.progress?.total ?? 6;

  return (
    <>
      {st && !st.dismissed && (
        <section className="rounded-2xl border border-[#1473E6]/25 bg-white p-5" data-testid="setup-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <Rocket className="h-4 w-4 text-[#1473E6]" />
              <h2 className="font-heading text-base font-extrabold text-slate-900">{t("setup_title")}</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#1473E6]" data-testid="setup-progress">
                {t("setup_progress", { done, total })}
              </span>
              <button onClick={dismiss} data-testid="setup-dismiss" aria-label={t("setup_dismiss")}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100" data-testid="setup-bar">
            <div className="h-full rounded-full bg-[#1473E6] transition-all" style={{ width: `${(done / total) * 100}%` }} />
          </div>
          {st.all_done ? (
            <div className="mt-3 rounded-xl border border-green-200 bg-green-50 p-3.5" data-testid="setup-done">
              <p className="text-sm font-bold text-green-700">{t("setup_done_title")}</p>
              <p className="mt-0.5 text-xs text-slate-500">{t("setup_done_sub")}</p>
            </div>
          ) : (
            <ul className="mt-3 space-y-1">
              {st.steps.map((s) => {
                const Icon = STEP_ICONS[s.key] || Circle;
                return (
                  <li key={s.key}>
                    <button onClick={() => runStep(s.key)} data-testid={`setup-step-${s.key}`}
                      className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50 active:scale-[0.99]">
                      {s.done
                        ? <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
                        : <Icon className="h-5 w-5 shrink-0 text-slate-300" />}
                      <span className={`flex-1 ${s.done ? "text-slate-400 line-through" : ""}`}>{t(`setup_step_${s.key}`)}</span>
                      {!s.done && <ChevronRight className="h-4 w-4 text-slate-300" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {!installed && (
        <button onClick={() => setInstallOpen(true)} data-testid="setup-install-row"
          className="flex min-h-[44px] w-full items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 active:scale-[0.99]">
          <Download className="h-4 w-4 text-brand" /> {t("setup_install_row")}
          <ChevronRight className="ml-auto h-4 w-4 text-slate-300" />
        </button>
      )}

      {["owner", "admin"].includes(user?.role) && !importDismissed && (
        <div className="relative">
          <button onClick={() => navigate("/app/settings/import")} data-testid="setup-import-row"
            className="flex min-h-[44px] w-full items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 active:scale-[0.99]">
            <Briefcase className="h-4 w-4 text-brand" /> {t("setup_import_row")}
            <ChevronRight className="ml-auto h-4 w-4 text-slate-300" />
          </button>
          <button onClick={() => { setImportDismissed(true); try { localStorage.setItem("bp_setup_import_dismissed", "1"); } catch {} }}
            aria-label={t("dismiss", "Dismiss")} data-testid="setup-import-dismiss"
            className="absolute right-10 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-300 hover:text-slate-500 active:scale-90">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {installOpen && createPortal(
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50" onClick={() => setInstallOpen(false)}
          data-testid="install-sheet">
          <div className="w-full max-w-md rounded-t-3xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom,0px))]"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-base font-extrabold text-slate-900">{t("setup_install_row")}</h3>
              <button onClick={() => setInstallOpen(false)} data-testid="install-sheet-close" aria-label={t("action_cancel")}
                className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            {isIos() ? (
              <div className="mt-3 space-y-3">
                <img src="/setup/ios-add-to-home.jpg" alt="" data-testid="install-ios-img"
                  className="mx-auto w-44 rounded-xl border border-slate-200" />
                <ol className="space-y-1.5 text-sm font-semibold text-slate-700">
                  <li>{t("setup_install_ios_1")}</li>
                  <li className="flex items-center gap-1.5">{t("setup_install_ios_2")} <Share className="h-3.5 w-3.5 text-brand" /></li>
                  <li>{t("setup_install_ios_3")}</li>
                </ol>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                <p className="text-sm text-slate-600">{t("setup_install_android_manual")}</p>
                <button onClick={() => { setInstallOpen(false); window.dispatchEvent(new Event("bf:show-install")); }}
                  data-testid="install-android-btn"
                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-brand px-5 text-sm font-bold text-white active:scale-95">
                  <Download className="h-4 w-4" /> {t("install_action")}
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
